import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Button,
    Card,
    CardContent,
    Grid,
    Avatar,
    styled,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    IconButton,
    Paper,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Chip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { getAppointmentProfileById, changeAppointmentPassword } from "../services/appointment";
import userLogo from "../assets/user-logo.svg";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PersonIcon from "@mui/icons-material/Person";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import InputAdornment from "@mui/material/InputAdornment";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import LockIcon from "@mui/icons-material/Lock";
import EditIcon from "@mui/icons-material/Edit";
import LanguageIcon from "@mui/icons-material/Language";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import EmojiEvents from "@mui/icons-material/EmojiEvents";
import Loop from "@mui/icons-material/Loop";

const FormContainer = styled(Card)(() => ({
    background: "white",
    borderRadius: "20px",
    padding: "30px",
    boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
    margin: "0 auto",
    maxWidth: "100%",
    position: "relative",
    textAlign: "center",
}));

const InfoCard = styled(Card)(() => ({
    borderRadius: "18px",
    boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
    background: "linear-gradient(135deg, #ffffff, #f9fafb)",
    transition: "all 0.3s ease",
    textAlign: "center",
    padding: "24px 16px",
}));

const HeaderContainer = styled(Box)(({ theme }) => ({
    background: 'white',
    borderRadius: '16px',
    padding: '16px 24px',
    marginBottom: '24px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
    border: '1px solid rgba(0,0,0,0.06)',
}));

const MainContainer = styled(Box)(({ theme }) => ({
    background: '#f8fafc',
    minHeight: '100vh',
    padding: '20px',
}));

export default function AppointmentCustomerProfile() {
    const navigate = useNavigate();
    const storedCustomer = JSON.parse(localStorage.getItem("customer") || "{}");
    const storedUser = JSON.parse(localStorage.getItem("user") || "{}");

    // Try to get appointment ID from different sources
    const appointmentId = storedCustomer?.id || storedUser?.id || localStorage.getItem("appointment_id");

    console.log("Stored customer:", storedCustomer);
    console.log("Stored user:", storedUser);
    console.log("Appointment ID:", appointmentId);

    const [appointmentProfile, setAppointmentProfile] = useState(null);
    const [resetPasswordOpen, setResetPasswordOpen] = useState(false);
    const [customerProfile, setCustomerProfile] = useState(null);
    const [role, setRole] = useState("");
    const [packageType, setPackageType] = useState("");
    const isSelf = role === "self";


    const displayName = isSelf
        ? `${customerProfile?.firstname || ""} ${customerProfile?.lastname || ""}`
        : appointmentProfile?.name || "";

    const displayEmail = isSelf
        ? customerProfile?.email
        : appointmentProfile?.email;

    const displayPhone = isSelf
        ? customerProfile?.phone
        : appointmentProfile?.phone;

    const displayGender = isSelf
        ? customerProfile?.gender
        : appointmentProfile?.gender;

    const displayRelation = isSelf
        ? "self"
        : appointmentProfile?.relation;

    // Password form data & errors
    const [passwordData, setPasswordData] = useState({
        current: "",
        new: "",
        confirm: "",
    });
    const [errors, setErrors] = useState({
        current: "",
        new: "",
        confirm: "",
    });

    const [showPassword, setShowPassword] = useState({
        current: false,
        new: false,
        confirm: false,
    });

    useEffect(() => {
        if (appointmentId) {
            const loadAppointmentProfile = async () => {
                try {
                    console.log("Loading appointment profile for ID:", appointmentId);
                    const data = await getAppointmentProfileById(appointmentId);
                    console.log("Appointment profile data:", data);
                    console.log("Appointment object:", data.appointment);
                    console.log("Customer object:", data.customer);
                    setAppointmentProfile(data.appointment);
                    setCustomerProfile(data.customer || null);
                    setRole(data.role);
                    setPackageType(data.package_type);
                } catch (error) {
                    console.error("Error loading appointment profile:", error);
                }
            };
            loadAppointmentProfile();
        }
    }, [appointmentId]);

    const handleClickShowPassword = (field) => {
        setShowPassword((prev) => ({
            ...prev,
            [field]: !prev[field],
        }));
    };

    const handlePasswordUpdate = async () => {
        try {
            // Reset previous errors
            setErrors({ current: "", new: "", confirm: "" });

            // Validation
            if (!passwordData.current || !passwordData.new || !passwordData.confirm) {
                setErrors({
                    current: !passwordData.current ? "Current password is required" : "",
                    new: !passwordData.new ? "New password is required" : "",
                    confirm: !passwordData.confirm ? "Confirm password is required" : "",
                });
                return;
            }

            if (passwordData.new !== passwordData.confirm) {
                setErrors({ confirm: "Passwords do not match" });
                return;
            }


            const response = await changeAppointmentPassword(
                appointmentId,
                passwordData.current,
                passwordData.new
            );

            // Success
            alert(response.message || "Password updated successfully!");

            setPasswordData({ current: "", new: "", confirm: "" });
            setResetPasswordOpen(false);

        } catch (error) {
            console.error("Password update failed:", error);

            setErrors({
                current:
                    error.response?.data?.error ||
                    error.response?.data?.message ||
                    "Old password incorrect",
            });
        }
    };

    const languageMap = {
        eng: "English",
        hin: "Hindi",
        tel: "Telugu",
        ben: "Bengali",
        kan: "Kannada",
    };

    const capitalizeWords = (str) => {
        if (!str) return str;
        return str.toLowerCase().replace(/\b\w/g, char => char.toUpperCase());
    };
    const fullName =
        customerProfile?.firstname
            ? `${capitalizeWords(customerProfile.firstname)} ${capitalizeWords(customerProfile.lastname || '')}`
            : capitalizeWords(appointmentProfile?.name || '');

    return (
        <MainContainer>
            <FormContainer>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 3,
                    }}
                >

                    <Typography variant="h6" sx={{ fontWeight: 700, color: "#0B2D6B" }}>
                        My Profile
                    </Typography>

                    <Button
                        variant="outlined"
                        startIcon={<LockIcon />}
                        onClick={() => setResetPasswordOpen(true)}
                        sx={{
                            textTransform: "none",
                            borderRadius: "8px",
                            borderColor: "#0B2D6B",
                            color: "#0B2D6B",
                            fontSize: "0.875rem",
                            "&:hover": {
                                backgroundColor: "#f0f7ff",
                                borderColor: "#08275a",
                            },
                        }}
                    >
                        Reset Password
                    </Button>
                </Box>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 3, textAlign: "center" }}>
                    {/* Basic Information */}
                    <Box
                        sx={{
                            p: 3,
                            borderRadius: "12px",
                            backgroundColor: "#f8fafc",
                            border: "1px solid #e2e8f0",
                        }}
                    >

                        <Typography
                            variant="h6"
                            sx={{
                                color: '#1a1238',
                                fontSize: { xs: '1rem', sm: '1.25rem' },
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                textAlign: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            <img
                                src={
                                    (customerProfile?.gender?.toLowerCase() === "male" || appointmentProfile?.gender?.toLowerCase() === "male")
                                        ? "/man.png"
                                        : (customerProfile?.gender?.toLowerCase() === "female" || appointmentProfile?.gender?.toLowerCase() === "female")
                                            ? "/woman.png"
                                            : userLogo
                                }
                                alt="gender-icon"
                                style={{ width: 24, height: 24 }}
                            />

                            {fullName.trim()}
                        </Typography>
                        <Typography sx={{ color: "#6b7280", fontSize: "0.95rem", mb: 2, textAlign: "center" }}>
                            Gender: {capitalizeWords(customerProfile?.gender || appointmentProfile?.gender || 'N/A')}
                        </Typography>

                        <Typography variant="h6" sx={{ mb: 2, color: "#374151", fontWeight: 600, textAlign: "center" }}>
                            Contact/Other Information
                        </Typography>
                        <Typography sx={{ color: "#6b7280", fontSize: "0.95rem", mb: 1, textAlign: "center" }}>
                            Email: {appointmentProfile?.email || 'N/A'} | Phone: {appointmentProfile?.phone || 'N/A'}
                        </Typography>
                        <Typography sx={{
                            color: '#666',
                            fontSize: { xs: '0.85rem', sm: '1rem' },
                            lineHeight: { xs: 1.6, sm: 1.5 },
                            mt: 1,
                            textAlign: 'center',
                        }}>
                            Account Type: {capitalizeWords(appointmentProfile?.cust_type || 'N/A')} | Status: {capitalizeWords(appointmentProfile?.status || 'N/A')}
                        </Typography>
                        <Typography sx={{
                            color: '#666',
                            fontSize: { xs: '0.85rem', sm: '1rem' },
                            lineHeight: { xs: 1.6, sm: 1.5 },
                            mt: 1,
                            textAlign: 'center',
                        }}>
                            {/* Patient ID: {appointmentProfile?.pid ?? 'N/A'} | */}
                            Booked Appointments: {appointmentProfile?.booked_appointment ?? 'N/A'} |
                            Relation: {capitalizeWords(appointmentProfile?.relation ?? 'N/A')}
                        </Typography>
                        {role === "self" && customerProfile && (
                            <Typography
                                sx={{
                                    color: "#666",
                                    fontSize: { xs: "0.85rem", sm: "1rem" },
                                    lineHeight: { xs: 1.6, sm: 1.5 },
                                    mt: 1,
                                    textAlign: "center",
                                }}
                            >
                                Date Of Birth:{" "}
                                {customerProfile?.date_of_birth ? new Date(customerProfile.date_of_birth).toLocaleDateString() : "N/A"}{" "}
                                | Preferred Language:{" "}
                                {languageMap[customerProfile?.preferred_language] || capitalizeWords('N/A')} |  Age: {customerProfile?.age || 'N/A'}
                            </Typography>
                        )}

                    </Box>






                </Box>



                {role === "self" && customerProfile && (

                    <Box
                        sx={{
                            p: 2,
                            mt: 4,
                            borderRadius: "12px",
                            backgroundColor: "#f8fafc",
                            border: "1px solid #e2e8f0",
                            textAlign: "center",
                        }}
                    >

                        <Grid container justifyContent="center">
                            <Grid item xs={12} md={10} lg={8}>
                                <Box sx={{ textAlign: "center" }}>
                                    <Box
                                        sx={{
                                            textAlign: "center",
                                        }}
                                    >
                                        <Typography variant="h6" sx={{ mb: 2, color: "#374151", fontWeight: 600, textAlign: "center" }}>
                                            Medical Information
                                        </Typography>
                                        <Typography sx={{ color: "#6b7280", fontSize: "0.95rem", textAlign: "center" }}>
                                            Assigned Doctor: {capitalizeWords(customerProfile?.assigned_doctor_name || 'N/A')} |
                                            Hospital: {capitalizeWords(customerProfile?.hospital || "N/A")} |
                                            Diagnosis: {capitalizeWords(customerProfile?.diagnosis || "N/A")} | Primary Diagnosis: {capitalizeWords(customerProfile?.primary_diagnosis || 'N/A')} | Stage: {capitalizeWords(customerProfile?.stage || 'N/A')} | Date of Diagnosis: {customerProfile?.date_of_diagnosis ? new Date(customerProfile.date_of_diagnosis).toLocaleDateString() : 'Invalid Date'} |
                                            Treatment Type: {capitalizeWords(customerProfile?.treatment_type || 'N/A')} | Treatment Start Date: {customerProfile?.treatment_start_date ? new Date(customerProfile.treatment_start_date).toLocaleDateString() : 'Invalid Date'} |
                                            No of cycles planned: {customerProfile?.no_of_cycles_planned || 'N/A'} | Current Cycle: {customerProfile?.current_cycle || 'N/A'}
                                        </Typography>

                                    </Box>
                                </Box>
                            </Grid>
                        </Grid>


                        {/* Caregiver Members */}
                        {/* {customerProfile?.members?.length > 0 && (
                            <Box sx={{ mt: 3 }}>
                                 <Typography variant="h6" sx={{ mb: 2, color: "#374151", fontWeight: 600, textAlign: "center" }}>
                                    Caregivers
                                </Typography>

                                {customerProfile.members.map((member) => (
                                    <Box
                                        key={member.id}
                                        sx={{
                                            // p: 2,
                                            // mb: 1,
                                            // borderRadius: "10px",
                                            // backgroundColor: "#f3f4f6",
                                        }}
                                    >
                                        <Typography sx={{ color: "#6b7280", fontSize: "0.95rem", textAlign: "center" }}> 
                                            Name: {capitalizeWords(member.name || '')}
                                        </Typography>
                                        <Typography sx={{ color: "#6b7280", fontSize: "0.95rem", textAlign: "center" }}>
                                            Relation: {capitalizeWords(member.relation || '')}
                                        </Typography>
                                        <Typography sx={{ color: "#6b7280", fontSize: "0.95rem", textAlign: "center" }}>
                                            Email: {member.email}
                                        </Typography>
                                        <Typography sx={{ color: "#6b7280", fontSize: "0.95rem", textAlign: "center" }}>
                                            Phone: {member.phone}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        )} */}
                    </Box>
                )}
                {/* {role !== "self" && (
                    <Box
                        sx={{
                            mt: 4,
                            p: 3,
                            borderRadius: "12px",
                            backgroundColor: "#f9fafb",
                            border: "1px solid #e5e7eb",
                            textAlign: "center",
                        }}
                    >
                        <Typography sx={{ textAlign: "center" }}>
                            You are logged in as Caregiver. Medical details are restricted.
                        </Typography>
                    </Box>
                )} */}

                {/* Reset Password Dialog */}
                <Dialog
                    open={resetPasswordOpen}
                    onClose={() => setResetPasswordOpen(false)}
                    fullWidth
                    maxWidth="xs"
                    PaperProps={{
                        sx: {
                            borderRadius: 4,
                            p: 2,
                            background: "#ffffff",
                            boxShadow: "0px 8px 30px rgba(0,0,0,0.15)",
                            width: "360px",
                        },
                    }}
                >
                    <DialogTitle
                        sx={{
                            textAlign: "center",
                            fontWeight: 700,
                            fontSize: "1.1rem",
                            color: "#0B2D6B",
                            pb: 0,
                        }}
                    >
                        Reset Password
                    </DialogTitle>

                    <Box sx={{ my: 2 }}>
                        <TextField
                            fullWidth
                            label="Current Password"
                            type={showPassword.current ? "text" : "password"}
                            InputLabelProps={{ shrink: true }}
                            variant="outlined"
                            value={passwordData.current}
                            onChange={(e) =>
                                setPasswordData({ ...passwordData, current: e.target.value })
                            }
                            error={Boolean(errors.current)}
                            helperText={errors.current}
                            required
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label="toggle current password visibility"
                                            onClick={() => handleClickShowPassword("current")}
                                            edge="end"
                                        >
                                            {showPassword.current ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Box>

                    <TextField
                        fullWidth
                        label="New Password"
                        type={showPassword.new ? "text" : "password"}
                        InputLabelProps={{ shrink: true }}
                        variant="outlined"
                        value={passwordData.new}
                        onChange={(e) =>
                            setPasswordData({ ...passwordData, new: e.target.value })
                        }
                        error={Boolean(errors.new)}
                        helperText={errors.new}
                        required
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label="toggle new password visibility"
                                        onClick={() => handleClickShowPassword("new")}
                                        edge="end"
                                    >
                                        {showPassword.new ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                            sx: { mb: 2 }
                        }}
                    />

                    <Box sx={{ my: 2 }}>
                        <TextField
                            fullWidth
                            label="Confirm Password"
                            type={showPassword.confirm ? "text" : "password"}
                            InputLabelProps={{ shrink: true }}
                            variant="outlined"
                            value={passwordData.confirm}
                            onChange={(e) => {
                                const confirmValue = e.target.value;
                                setPasswordData({ ...passwordData, confirm: confirmValue });

                                // Live validation for confirm password matching new password
                                if (confirmValue !== passwordData.new) {
                                    setErrors((prev) => ({ ...prev, confirm: "Passwords do not match" }));
                                } else {
                                    setErrors((prev) => ({ ...prev, confirm: "" }));
                                }
                            }}
                            error={Boolean(errors.confirm)}
                            helperText={errors.confirm}
                            required
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label="toggle confirm password visibility"
                                            onClick={() => handleClickShowPassword("confirm")}
                                            edge="end"
                                        >
                                            {showPassword.confirm ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Box>

                    <DialogActions
                        sx={{
                            px: 2,
                            pb: 2,
                            display: "flex",
                            justifyContent: "flex-end",
                            gap: 1.5,
                        }}
                    >
                        <Button
                            variant="text"
                            sx={{
                                textTransform: "none",
                                color: "#6b7280",
                                "&:hover": { backgroundColor: "#f5f5f5" },
                            }}
                            onClick={() => setResetPasswordOpen(false)}
                        >
                            Cancel
                        </Button>

                        <Button
                            variant="contained"
                            sx={{
                                textTransform: "none",
                                borderRadius: 2,
                                backgroundColor: "#0B2D6B",
                                px: 3,
                                "&:hover": { backgroundColor: "#08295F" },
                            }}
                            onClick={handlePasswordUpdate}
                        >
                            Update
                        </Button>
                    </DialogActions>
                </Dialog>
                {/* Stats Cards */}
                {/* <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: {
                            xs: "1fr",
                            sm: "repeat(2, 1fr)",
                        },
                        gap: 3,
                        mt: 4,
                        mb: 2,
                    }}
                >
                    <InfoCard
                        sx={{
                            py: 2.5,
                            px: 2,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            minHeight: "140px",
                        }}
                    >
                        <EmojiEvents sx={{ fontSize: 34, color: "#3b82f6", mb: 1 }} />
                        <Typography variant="subtitle2" sx={{ color: "#6b7280", mb: 0.3 }}>
                            Sessions Completed
                        </Typography>
                        <Typography
                            variant="h5"
                            fontWeight={700}
                            sx={{ color: "#111827" }}
                        >
                            {customerProfile?.treatment_cycle ? customerProfile.treatment_cycle - 1 : 'NaN'}
                        </Typography>
                    </InfoCard>

                    <InfoCard
                        sx={{
                            py: 2.5,
                            px: 2,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            minHeight: "140px",
                        }}
                    >
                        <Loop sx={{ fontSize: 34, color: "#10b981", mb: 1 }} />
                        <Typography variant="subtitle2" sx={{ color: "#6b7280", mb: 0.3 }}>
                            Current Cycle
                        </Typography>
                        <Typography
                            variant="h5"
                            fontWeight={700}
                            sx={{ color: "#111827" }}
                        >
                            {customerProfile?.treatment_cycle || 'NaN'}
                        </Typography>
                    </InfoCard>
                </Box> */}
            </FormContainer>
        </MainContainer>
    );
}
